import { NavLink } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useShop } from '@/hooks/useShop';
import { APP_NAME, APP_TAGLINE } from '@/lib/brand';

const navItems = [
  { path: '/', label: 'Dashboard', icon: 'ri-dashboard-3-line', exact: true },
  { path: '/loans/create', label: 'Tạo Khoản Vay', icon: 'ri-file-add-line' },
  { path: '/loans', label: 'Quản Lý Khoản Vay', icon: 'ri-file-list-3-line' },
  { path: '/customers', label: 'Hồ Sơ Khách Hàng', icon: 'ri-contacts-book-2-line' },
  { path: '/daily-revenue', label: 'Doanh Thu Hàng Ngày', icon: 'ri-money-dollar-circle-line' },
  { path: '/confiscations', label: 'Tịch Thu', icon: 'ri-alarm-warning-line' },
  { path: '/vehicle-prices', label: 'Giá Xe', icon: 'ri-motorbike-line' },
  { path: '/activity', label: 'Lịch Sử Hoạt Động', icon: 'ri-history-line' },
  { path: '/reports', label: 'Báo Cáo', icon: 'ri-bar-chart-2-line' },
  { path: '/settings', label: 'Cài Đặt', icon: 'ri-settings-4-line' },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { user, isAdmin, isSuperAdmin, signOut, displayName: profileName } = useAuth();
  const { shop } = useShop();
  // super_admin chưa tạo tiệm riêng → chỉ thấy menu quản lý; có tiệm → thấy đủ như chủ tiệm
  const showShopMenu = !isSuperAdmin || !!shop;

  const handleLogout = () => {
    // Xóa toàn bộ localStorage trước để kill session ngay lập tức
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') || key.includes('supabase') || key.startsWith('__cache_')) {
        localStorage.removeItem(key);
      }
    });
    // Gọi signOut ở background (không chờ)
    signOut().catch(() => {});
    // Force reload trang login — session đã xóa nên sẽ hiển thị đăng nhập
    window.location.replace('/login');
  };

  const displayName = profileName || user?.email?.split('@')[0] || 'User';
  const avatarChar = displayName.charAt(0).toUpperCase();

  return (
    <>
      {/* Overlay on mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-screen w-64 bg-[#1a2332] flex flex-col z-30 select-none transition-transform duration-300
          ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl overflow-hidden bg-white/10 shrink-0">
            {shop?.logoUrl ? (
              <img src={shop.logoUrl} alt="Logo" className="w-full h-full object-contain" />
            ) : (
              <i className="ri-store-2-line text-xl text-[#f59e0b]"></i>
            )}
          </div>
          <div>
            <p className="text-white font-bold text-base leading-tight truncate max-w-[140px]">{shop?.name || APP_NAME}</p>
            <p className="text-white/40 text-xs">{APP_TAGLINE}</p>
          </div>
          {/* Close button on mobile */}
          <button
            onClick={onClose}
            className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors cursor-pointer lg:hidden"
          >
            <i className="ri-close-line text-white/60 text-lg"></i>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <p className="text-white/30 text-[10px] font-semibold uppercase tracking-widest px-3 mb-3">Menu chính</p>
          <ul className="space-y-1">
            {(showShopMenu ? navItems : []).map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.exact}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-[#f59e0b]/15 text-[#f59e0b] border-l-[3px] border-[#f59e0b] pl-[9px]'
                        : 'text-white/60 hover:text-white hover:bg-white/8'
                    }`
                  }
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  {item.label}
                </NavLink>
              </li>
            ))}
            {showShopMenu && (isAdmin || isSuperAdmin) && (
              <li>
                <NavLink
                  to="/staff"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-[#f59e0b]/15 text-[#f59e0b] border-l-[3px] border-[#f59e0b] pl-[9px]'
                        : 'text-white/60 hover:text-white hover:bg-white/8'
                    }`
                  }
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-shield-user-line text-lg"></i>
                  </span>
                  Quản Lý Nhân Viên
                </NavLink>
              </li>
            )}
            {isSuperAdmin && !shop && (
              <li>
                <NavLink
                  to="/setup-shop"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-[#f59e0b]/15 text-[#f59e0b] border-l-[3px] border-[#f59e0b] pl-[9px]'
                        : 'text-white/60 hover:text-white hover:bg-white/8'
                    }`
                  }
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-store-3-line text-lg"></i>
                  </span>
                  Tạo Cửa Hàng Của Tôi
                </NavLink>
              </li>
            )}
            {isSuperAdmin && (
              <li>
                <NavLink
                  to="/admin/shops"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-[#f59e0b]/15 text-[#f59e0b] border-l-[3px] border-[#f59e0b] pl-[9px]'
                        : 'text-white/60 hover:text-white hover:bg-white/8'
                    }`
                  }
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-store-3-line text-lg"></i>
                  </span>
                  Quản Lý Cửa Hàng
                </NavLink>
              </li>
            )}
          </ul>
        </nav>

        {/* User */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#f59e0b] flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-sm">{avatarChar}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate">{displayName}</p>
              <p className="text-white/40 text-xs truncate">{user?.email ?? ''}</p>
            </div>
            <button
              onClick={handleLogout}
              title="Đăng xuất"
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer text-white/50 hover:text-white/80"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-r-line text-sm"></i>
              </span>
              <span className="text-xs font-medium">Đăng xuất</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}